// library imports
import { Table, Button } from "antd";
import { useState, useEffect, useContext } from "react";
import {
  LoadingOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// style imports
import "../../styles/SystemReport/DailyReportContent.css";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// component imports
import { HorizontalDateMonthYearFilter } from "../common/DateFilter";
// config imports
import { MICROGRID_SITE_IDS } from "../../configs/configs";

const { Column, ColumnGroup } = Table;

function DailyReportContent() {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  const [dateSelection, setDateSelection] = useState(dayjs());
  const [data, setData] = useState([]);

  const formatToTwoDecimalPlaces = (value) => {
    if (value === undefined || value === null || value === "-") {
      return value;
    }
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /** API to get daily report */
  function getDailyReportData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "daily_report/getDailyReportData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        date_graph: dateSelection.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      },

      onSuccess: (response) => {
        setData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get daily report data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  }

  /** API to download daily report */
  function downloadDailyReport() {
    setSaveLoading(true);

    fetchWrapper({
      endpoint_url: "daily_report/downloadDailyReport",
      method: "POST",
      json_output: false,
      body: {
        table_data: data,
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `DailyReport_${dateSelection.format("YYYY-MM")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setSaveLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getDailyReportData();
    }
  }, [siteSelected, dateSelection]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  return (
    <>
      <div className="daily-report-date-selection">
        <Button
          className="daily-report-download-button"
          onClick={() => {
            downloadDailyReport();
          }}
          disabled={loading}
        >
          {saveLoading ? <LoadingOutlined /> : <VerticalAlignBottomOutlined />}
        </Button>
        <HorizontalDateMonthYearFilter
          startDateSelection={dateSelection}
          setStartDateSelection={setDateSelection}
          onFilter={() => {}}
          pickerType={"month"}
          showTitle={false}
          minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
          maxDate={dayjs()}
        />
      </div>
      {MICROGRID_SITE_IDS.includes(siteSelected) ? (
        <Table
          size="small"
          bordered
          dataSource={data}
          scroll={{ x: 800 }}
          loading={loading}
        >
          <Column title="Date" dataIndex="Date" key="Date" />
          <Column title="Load Consumption" dataIndex="Load" key="Load" />
          <ColumnGroup title="Energy Supply">
            <Column title="Solar Generation" dataIndex="Solar" key="Solar" />
            <Column title="Genset Supply" dataIndex="Genset" key="Genset" />
            <Column
              title="BESS Supply"
              dataIndex="BessDischarge"
              key="BessDischarge"
            />
          </ColumnGroup>
          <Column title="BESS Charge" dataIndex="BessCharge" key="BessCharge" />
        </Table>
      ) : (
        <Table
          size="small"
          bordered
          dataSource={data}
          scroll={{ x: 800 }}
          loading={loading}
        >
          <Column title="Date" dataIndex="date" key="date" />
          <Column
            title="Energy Generation (kWh)"
            dataIndex="energyGeneration"
            key="energyGeneration"
            render={(value) => formatToTwoDecimalPlaces(value)}
          />
          <ColumnGroup title="Energy Utilization">
            <Column
              title="PV Self Consumption (kWh)"
              dataIndex="pvSelfConsumption"
              key="pvSelfConsumption"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Self Consumption Ratio"
              dataIndex="selfConsumptionRatio"
              key="selfConsumptionRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PV Export (kWh)"
              dataIndex="pvExport"
              key="pvExport"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PV Energy Export Ratio"
              dataIndex="pvEnergyExportRatio"
              key="pvEnergyExportRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Import From Grid (kWh)"
              dataIndex="importFromGrid"
              key="importFromGrid"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
        </Table>
      )}
    </>
  );
}

export default DailyReportContent;
